import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styled, useTheme } from '@streem/ui-react';
import { useRef, forwardRef, useEffect } from 'react';
import { LoadingScreen } from './loading_screen';
import { useMultiplexer } from '../../../hooks/use_multiplexer';
import { useRoomSession } from '../../../context/room_session';
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
export const VideoFramePreview = forwardRef(({ videoController, animating }, parentRef) => {
    const theme = useTheme();
    const roomSession = useRoomSession();
    /** Multiplexer */
    const canvasContainerRef = useRef(null);
    const { width: canvasWidth, height: canvasHeight } = useMultiplexer(roomSession, videoController, canvasContainerRef) ?? { width: 1280, height: 720 };
    const portrait = canvasWidth < canvasHeight;
    const aspectRatioConversion = portrait
        ? canvasHeight / canvasWidth
        : canvasWidth / canvasHeight;
    const dataTestId = 'video-frame-preview-test-id';
    useEffect(() => {
        if (!window.ResizeObserver) {
            // polyfill for older browsers or disabled iOS Safari setting
            window.ResizeObserver = ResizeObserverPolyfill;
        }
        const parentResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const parentElement = entry.target;
                const parentHeight = entry.contentRect.height;
                const parentWidth = entry.contentRect.width;
                parentElement.style.setProperty('--canvas-height', `${parentHeight}px`);
                parentElement.style.setProperty('--canvas-width', `${parentWidth}px`);
            }
        });
        parentResizeObserver.observe(parentRef.current);
        if (animating) {
            parentResizeObserver.disconnect();
        }
        return () => parentResizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [animating]);
    // The multiplexer sets an 'object-fit: contain' property on the canvas element.
    // This causes the canvas to be scaled to fit inside the parent element while maintaining aspect ratio.
    // We want to avoid this for the zoomed-in lobby preview video, so we remove the property and manage the aspect ratio ourselves
    useEffect(() => {
        if (canvasContainerRef && canvasContainerRef.current && !animating) {
            const canvas = canvasContainerRef.current.querySelector('canvas');
            if (canvas && canvas.style.objectFit === 'contain') {
                canvas.style.objectFit = '';
            }
        }
    });
    useEffect(() => {
        if (animating && canvasContainerRef && canvasContainerRef.current) {
            const windowWidth = window.innerWidth;
            const { width: canvasWidth, height: canvasHeight } = canvasContainerRef.current.getBoundingClientRect();
            let scaledWidth;
            let scaledHeight;
            if (!portrait) {
                // if video is landscape, let it be as wide as it wants -- height === width / aspectRatioConversion
                scaledWidth = windowWidth;
                scaledHeight = windowWidth / aspectRatioConversion;
            }
            else {
                // if video is portrait, let it be as tall as it wants --- width === height / aspectRatioConversion
                scaledHeight = window.innerHeight;
                scaledWidth = window.innerHeight / aspectRatioConversion;
            }
            const startingKeyframe = {
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
            };
            const endingKeyframe = {
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
            };
            canvasContainerRef.current.animate([startingKeyframe, endingKeyframe], {
                duration: 350,
                fill: 'forwards',
            });
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [animating]);
    return (_jsxs(VideoContainer, { theme: theme, "data-testid": dataTestId, ref: parentRef, children: [_jsx(CanvasWrapper, { portrait: portrait, aspectRatioConversion: aspectRatioConversion, "data-testid": "canvas-wrapper", ref: canvasContainerRef }), _jsx(LoadingScreen, { videoController: videoController })] }));
});
const CanvasWrapper = styled.div(({ portrait, aspectRatioConversion }) => {
    /**
     * The multiplexer automatically fits the canvas video dimensions to its container constaints.
     * During a call, we want to see the entire video frame, so we always define a width and height, which the multiplexer fills with the video canvas.
     * Any unused space is filled with a dark background, producing a letterbox effect.
     * In the lobby, the video frame preview is small, and a letterbox is not desirable since it shrinks the video so small.
     * So, we eliminate the letterbox here by allowing the longer dimension (width or height) to be 'undefined' so the multiplexer fills the video canvas to fit the defined '100%' width or height.
     * Any canvas overflow stretches outside of the container where it is not visible (hidden by 'VideoContainer' styles).
     * By optimizing for a zoomed-in center video preview, this ensures you never see black borders on the sides or top of the video frame preview.
     */
    return {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        height: portrait
            ? `calc(var(--canvas-width) * ${aspectRatioConversion})`
            : 'var(--canvas-height)',
        width: portrait
            ? 'var(--canvas-width)'
            : `calc(var(--canvas-height) * ${aspectRatioConversion})`,
    };
});
const VideoContainer = styled.div(props => {
    // These are default styles for stacked view. Prop styles will override when in face to world.
    return {
        width: '100%',
        height: `100%`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Parent must use 'position: relative'
        // This will allow easier animation transitions when we get around to implementing them.
        position: 'absolute',
        borderRadius: '20px',
        background: props.theme.colors.black,
        ...props.styles,
        overflow: 'hidden',
        // Needed for rounded corners to work with desktop Safari.
        backfaceVisibility: 'hidden',
        transform: 'translate3d(0,0,0)',
    };
});
//# sourceMappingURL=video_frame_preview.js.map